import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import dove from 'assets/themes/0209/group_bird.svg';
import nationalDayImage from 'assets/themes/0209/footer_right.png';
import { isDashboardRoute } from './isDashboardRoute';

const EFFECT_Z_INDEX = 11;

/**
 * Màu pháo hoa — mỗi rocket lấy 4–5 màu khác nhau trong 1 quả nổ.
 */
const FIREWORK_COLORS = [
    '#FFD700',
    '#FF3B30',
    '#5AC8FA',
    '#FF9500',
    '#FF2D55',
    '#FFE566',
    '#7EC8FF',
];

/**
 * ============================================================
 * FIREWORK CONFIG
 * ============================================================
 */

const SHOW_MS = 8500;

/** Số rocket mỗi lần bắn (trừ lần cuối) */
const ROCKETS_PER_BURST_MIN = 1;
const ROCKETS_PER_BURST_MAX = 3;
const FINAL_BURST_ROCKET_COUNT = 3;

/**
 * Khoảng cách giữa các đợt bắn.
 *
 * 0s
 * 1.8s
 * 3.6s
 * 5.4s
 * 9.2s (lần cuối — luôn 3 rocket)  
 */
const BURST_TIMES = [
    0,
    1800,
    3600,
    5400,
    7200,
    9200,
];

/**
 * 15 phút bắn lại một show.
 */
const PERIOD_MS = 15 * 60 * 1000;

/**
 * Trọng lực của rocket.
 */
const ROCKET_GRAVITY = 620;

/** Kích thước rocket bay lên (head + trail) */
const ROCKET_HEAD_OUTER_RADIUS = 7.5;
const ROCKET_HEAD_INNER_RADIUS = 2.4;
const ROCKET_HEAD_GLOW_BLUR = 18;
const ROCKET_TRAIL_LINE_WIDTH = 4.4;
const ROCKET_TRAIL_MAX_POINTS = 10;

/** Kích thước vụ nổ pháo hoa (particles) */
const EXPLOSION_PARTICLE_COUNT_MIN = 66;
const EXPLOSION_PARTICLE_COUNT_MAX = 88;
const EXPLOSION_PARTICLE_SPEED_MIN = 100;
const EXPLOSION_PARTICLE_SPEED_MAX = 350;
const EXPLOSION_PARTICLE_SIZE_MIN = 2.2;
const EXPLOSION_PARTICLE_SIZE_MAX = 4.8;
const EXPLOSION_PARTICLE_SHADOW_BLUR = 8;

/**
 * Trọng lực của particles sau khi nổ.
 * Thấp hơn → rơi chậm, loang lâu hơn.
 */
const PARTICLE_GRAVITY = 220;

/**
 * ============================================================
 * UTILS
 * ============================================================
 */

function random(min, max) {
    return min + Math.random() * (max - min);
}

function pickPalette() {
    const copy = FIREWORK_COLORS.slice();
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = copy[i];
        copy[i] = copy[j];
        copy[j] = tmp;
    }
    const count = Math.floor(random(4, 6));
    return copy.slice(0, count);
}

/**
 * ============================================================
 * CREATE ROCKET
 * ============================================================
 *
 * Rocket bắt đầu từ gần đáy màn hình.
 *
 * Sau đó:
 *
 *     🚀
 *      ↑
 *      ↑
 *      ↑
 *      ↑
 *      💥
 */
function createRocket(width, height) {
    const startX = random(
        width * 0.18,
        width * 0.82
    );

    const targetY = random(
        height * 0.14,
        height * 0.49
    );

    /**
     * Thời gian rocket bay lên.
     * Khoảng 1.15 → 1.45 giây.
     */
    const flightTime = random(1.15, 1.45);

    /**
     * Gravity.
     */
    const gravity = ROCKET_GRAVITY;

    /**
     * Tính vận tốc ban đầu để rocket
     * đạt targetY sau flightTime.
     *
     * y = y0 + v0*t + 1/2*g*t²
     */
    const distance = targetY - height;

    const vy =
        (
            distance -
            0.5 * gravity * flightTime * flightTime
        ) / flightTime;

    return {
        type: 'rocket',

        x: startX,
        y: height + 10,

        vx: random(-20, 20),
        vy,

        targetY,

        age: 0,

        /**
         * Rocket tồn tại tối đa.
         */
        maxAge: flightTime + 0.3,

        palette: pickPalette(),

        /**
         * Vệt rocket.
         */
        trail: [],
    };
}

/**
 * ============================================================
 * CREATE EXPLOSION
 * ============================================================
 */
function createExplosion(x, y, palette) {
    const particles = [];

    /**
     * 42 → 65 particles
     *
     * Không quá nhiều để tránh màn hình bị rối.
     */
    const count = Math.floor(
        random(
            EXPLOSION_PARTICLE_COUNT_MIN,
            EXPLOSION_PARTICLE_COUNT_MAX
        )
    );

    /**
     * Random kiểu nổ.
     *
     * Một số quả nổ dạng tròn.
     */
    const rotation = random(0, Math.PI * 2);

    for (let i = 0; i < count; i += 1) {
        const angle =
            rotation +
            (Math.PI * 2 * i) / count +
            random(-0.08, 0.08);

        /**
         * Tốc độ particle sau khi nổ.
         *
         * Chậm hơn effect cũ rất nhiều.
         */
        const speed = random(
            EXPLOSION_PARTICLE_SPEED_MIN,
            EXPLOSION_PARTICLE_SPEED_MAX
        );

        const color =
            palette[
                Math.floor(
                    Math.random() * palette.length
                )
            ];

        particles.push({
            type: 'particle',

            x,
            y,

            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,

            /**
             * Lifetime tính bằng giây.
             */
            life: random(2.2, 3.4),

            maxLife: random(2.2, 3.4),

            size: random(
                EXPLOSION_PARTICLE_SIZE_MIN,
                EXPLOSION_PARTICLE_SIZE_MAX
            ),

            color,

            /**
             * Một số particle có trail.
             */
            trail: Math.random() > 0.42,

            /**
             * Slight twinkle.
             */
            twinkle: Math.random() > 0.65,

            twinkleOffset: Math.random() * Math.PI * 2,
        });
    }

    return particles;
}
function NationalDayFireworks() {
    const canvasRef = useRef(null);

    const rocketsRef = useRef([]);
    const particlesRef = useRef([]);

    const rafRef = useRef(0);

    const showTimersRef = useRef([]);
    const periodRef = useRef(0);
    const showGenerationRef = useRef(0);

    useEffect(() => {
        const canvas = canvasRef.current;

        if (!canvas) {
            return undefined;
        }

        const ctx = canvas.getContext('2d');

        if (!ctx) {
            return undefined;
        }

        /**
         * ======================================================
         * RESIZE
         * ======================================================
         */

        const resize = () => {
            const dpr = Math.min(
                window.devicePixelRatio || 1,
                2
            );

            const width = window.innerWidth;
            const height = window.innerHeight;

            canvas.width = width * dpr;
            canvas.height = height * dpr;

            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;

            ctx.setTransform(
                dpr,
                0,
                0,
                dpr,
                0,
                0
            );
        };

        resize();

        window.addEventListener(
            'resize',
            resize
        );

        /**
         * ======================================================
         * LAUNCH ROCKET
         * ======================================================
         */

        const launchRockets = (count) => {
            const width = window.innerWidth;
            const height = window.innerHeight;

            for (let i = 0; i < count; i += 1) {
                rocketsRef.current.push(
                    createRocket(
                        width,
                        height
                    )
                );
            }
        };

        const getBurstRocketCount = (burstIndex) => {
            const isFinalBurst =
                burstIndex ===
                BURST_TIMES.length - 1;

            if (isFinalBurst) {
                return FINAL_BURST_ROCKET_COUNT;
            }

            return Math.floor(
                random(
                    ROCKETS_PER_BURST_MIN,
                    ROCKETS_PER_BURST_MAX + 1
                )
            );
        };

        const clearPeriodTimer = () => {
            if (periodRef.current) {
                window.clearTimeout(
                    periodRef.current
                );
                periodRef.current = 0;
            }
        };

        /**
         * ======================================================
         * CLEAR TIMERS
         * ======================================================
         */

        const clearShowTimers = () => {
            showTimersRef.current.forEach(
                (id) => {
                    window.clearTimeout(id);
                }
            );

            showTimersRef.current = [];
        };

        /**
         * ======================================================
         * RUN ONE SHOW
         * ======================================================
         */

        const runShow = () => {
            clearShowTimers();

            /**
             * Reset state cũ để show mới không chồng lên
             * rocket/particle còn sót (interval trùng, tab wake, v.v.).
             */
            showGenerationRef.current += 1;
            const showGeneration =
                showGenerationRef.current;

            rocketsRef.current = [];
            particlesRef.current = [];

            BURST_TIMES.forEach(
                (elapsed, burstIndex) => {
                    const rocketCount =
                        getBurstRocketCount(
                            burstIndex
                        );

                    const timer =
                        window.setTimeout(
                            () => {
                                if (
                                    showGeneration !==
                                    showGenerationRef.current
                                ) {
                                    return;
                                }

                                launchRockets(
                                    rocketCount
                                );
                            },
                            elapsed
                        );

                    showTimersRef.current.push(
                        timer
                    );
                }
            );
        };

        const scheduleNextShow = () => {
            clearPeriodTimer();

            periodRef.current =
                window.setTimeout(() => {
                    runShow();
                    scheduleNextShow();
                }, PERIOD_MS);
        };

        /**
         * ======================================================
         * ANIMATION LOOP
         * ======================================================
         */

        let lastTime = performance.now();

        const draw = (now) => {
            /**
             * delta time tính bằng giây.
             *
             * Giới hạn max 40ms để tránh trường hợp
             * browser bị lag rồi particle nhảy quá xa.
             */
            const dt = Math.min(
                (now - lastTime) / 1000,
                0.04
            );

            lastTime = now;

            const width = window.innerWidth;
            const height = window.innerHeight;

            ctx.clearRect(
                0,
                0,
                width,
                height
            );

            /**
             * ==================================================
             * ROCKETS
             * ==================================================
             */

            const nextRockets = [];

            for (
                const rocket of rocketsRef.current
            ) {
                rocket.age += dt;

                /**
                 * Lưu vị trí cũ cho trail.
                 */
                rocket.trail.push({
                    x: rocket.x,
                    y: rocket.y,
                });

                /**
                 * Chỉ giữ 8 điểm trail.
                 */
                if (
                    rocket.trail.length >
                    ROCKET_TRAIL_MAX_POINTS
                ) {
                    rocket.trail.shift();
                }

                /**
                 * Physics.
                 */
                rocket.x +=
                    rocket.vx * dt;

                rocket.y +=
                    rocket.vy * dt;

                /**
                 * Gravity.
                 *
                 * Rocket càng lên cao
                 * càng giảm tốc.
                 */
                rocket.vy +=
                    ROCKET_GRAVITY * dt;

                /**
                 * Một chút drift.
                 */
                rocket.vx *=
                    Math.pow(
                        0.995,
                        dt * 60
                    );

                /**
                 * =================================================
                 * KIỂM TRA ĐÃ ĐẾN ĐIỂM NỔ
                 * =================================================
                 */

                if (
                    rocket.y <=
                        rocket.targetY ||
                    rocket.vy >= -20 ||
                    rocket.age >=
                        rocket.maxAge
                ) {
                    /**
                     * 💥 NỔ
                     */
                    const explosion =
                        createExplosion(
                            rocket.x,
                            rocket.y,
                            rocket.palette
                        );

                    particlesRef.current.push(
                        ...explosion
                    );

                    continue;
                }

                /**
                 * =================================================
                 * DRAW ROCKET TRAIL
                 * =================================================
                 */

                if (
                    rocket.trail.length > 1
                ) {
                    ctx.beginPath();

                    rocket.trail.forEach(
                        (point, index) => {
                            const alpha =
                                (index + 1) /
                                rocket.trail.length;

                            ctx.globalAlpha =
                                alpha * 0.45;

                            if (index === 0) {
                                ctx.moveTo(
                                    point.x,
                                    point.y
                                );
                            } else {
                                ctx.lineTo(
                                    point.x,
                                    point.y
                                );
                            }
                        }
                    );

                    ctx.strokeStyle =
                        rocket.palette[0];

                    ctx.lineWidth = ROCKET_TRAIL_LINE_WIDTH;

                    ctx.stroke();

                    ctx.globalAlpha = 1;
                }

                /**
                 * =================================================
                 * DRAW ROCKET HEAD
                 * =================================================
                 */

                ctx.globalAlpha = 1;

                ctx.fillStyle =
                    '#FFFFFF';

                ctx.beginPath();

                ctx.arc(
                    rocket.x,
                    rocket.y,
                    ROCKET_HEAD_OUTER_RADIUS,
                    0,
                    Math.PI * 2
                );

                ctx.fill();

                /**
                 * Glow.
                 */
                ctx.shadowBlur = ROCKET_HEAD_GLOW_BLUR;
                ctx.shadowColor =
                    rocket.palette[0];

                ctx.fillStyle =
                    rocket.palette[0];

                ctx.beginPath();

                ctx.arc(
                    rocket.x,
                    rocket.y,
                    ROCKET_HEAD_INNER_RADIUS,
                    0,
                    Math.PI * 2
                );

                ctx.fill();

                ctx.shadowBlur = 0;

                nextRockets.push(
                    rocket
                );
            }

            rocketsRef.current =
                nextRockets;

            /**
             * ==================================================
             * EXPLOSION PARTICLES
             * ==================================================
             */

            const nextParticles = [];

            for (
                const p of
                    particlesRef.current
            ) {
                p.life -= dt;

                if (p.life <= 0) {
                    continue;
                }

                /**
                 * Physics.
                 */

                p.x += p.vx * dt;

                p.y += p.vy * dt;

                /**
                 * Trọng lực.
                 *
                 * Đây là phần tạo cảm giác
                 * particles "rơi xuống".
                 */
                p.vy +=
                    PARTICLE_GRAVITY *
                    dt;

                /**
                 * Air resistance.
                 */
                const drag =
                    Math.pow(
                        0.978,
                        dt * 60
                    );

                p.vx *= drag;
                p.vy *= drag;

                /**
                 * =================================================
                 * FADE OUT
                 * =================================================
                 */

                const lifeRatio =
                    p.life /
                    p.maxLife;

                /**
                 * Fade nhẹ ở cuối.
                 */
                let alpha =
                    Math.min(
                        lifeRatio * 2,
                        1
                    );

                /**
                 * Twinkle nhẹ.
                 */
                if (p.twinkle) {
                    const twinkle =
                        0.78 +
                        Math.sin(
                            now * 0.012 +
                                p.twinkleOffset
                        ) *
                            0.22;

                    alpha *= twinkle;
                }

                ctx.globalAlpha =
                    Math.max(
                        0,
                        alpha
                    );

                /**
                 * =================================================
                 * PARTICLE TRAIL
                 * =================================================
                 */

                if (p.trail) {
                    ctx.beginPath();

                    ctx.moveTo(
                        p.x,
                        p.y
                    );

                    ctx.lineTo(
                        p.x -
                            p.vx * 0.035,
                        p.y -
                            p.vy * 0.035
                    );

                    ctx.strokeStyle =
                        p.color;

                    ctx.lineWidth =
                        Math.max(
                            p.size * 0.45,
                            0.5
                        );

                    ctx.stroke();
                }

                /**
                 * =================================================
                 * PARTICLE
                 * =================================================
                 */

                ctx.fillStyle =
                    p.color;

                ctx.shadowBlur =
                    EXPLOSION_PARTICLE_SHADOW_BLUR;

                ctx.shadowColor =
                    p.color;

                ctx.beginPath();

                ctx.arc(
                    p.x,
                    p.y,
                    p.size,
                    0,
                    Math.PI * 2
                );

                ctx.fill();

                ctx.shadowBlur = 0;

                ctx.globalAlpha = 1;

                nextParticles.push(p);
            }

            particlesRef.current =
                nextParticles;

            /**
             * ==================================================
             * NEXT FRAME
             * ==================================================
             */

            rafRef.current =
                window.requestAnimationFrame(
                    draw
                );
        };

        /**
         * Bắn ngay khi effect mount.
         */
        runShow();

        /**
         * Sau đó mỗi 15 phút (setTimeout nối tiếp — tránh
         * setInterval bắn dồn khi tab background / interval trùng).
         */
        scheduleNextShow();

        rafRef.current =
            window.requestAnimationFrame(
                draw
            );

        /**
         * ======================================================
         * CLEANUP
         * ======================================================
         */

        return () => {
            window.removeEventListener(
                'resize',
                resize
            );

            clearPeriodTimer();

            clearShowTimers();

            window.cancelAnimationFrame(
                rafRef.current
            );

            rocketsRef.current = [];

            particlesRef.current = [];
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{
                position: 'fixed',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: EFFECT_Z_INDEX,
            }}
        />
    );
}

export default function NationalDayEffect() {
    const { pathname } = useLocation();
    const showFireworks = isDashboardRoute(pathname);

    return (
        <div className="theme-effects-layer" aria-hidden>
            {showFireworks ? <NationalDayFireworks /> : null}

            <img
                src={dove}
                alt=""
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 360,
                    width: '100%',
                    height: '138px',
                    pointerEvents: 'none',
                    zIndex: EFFECT_Z_INDEX,
                }}
             /> 
              <img
                src={nationalDayImage}
                alt=""
                className="national-day-footer-pin"
                style={{
                    // absolute trong .theme-effects-layer (fixed) → ghim góc phải khi scroll
                    position: 'fixed',
                    right: 0,
                    bottom: 0,
                    width: 120,
                    height: 'auto',
                    zIndex: EFFECT_Z_INDEX,
                    pointerEvents: 'none',
                }}
            />
        </div>
    );
}
